import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Menu, MenuItem, Avatar, Box,
  Chip, Tooltip, Divider, Paper, ListItemIcon,
  ListItemText, InputBase, alpha, Popover, Grid,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Search as SearchIcon,
  KeyboardCommandKey as CmdIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  HelpOutline,
  AppsRounded as AppsIcon,
  Inventory2 as ErpIcon,
} from '@mui/icons-material';
import useAuth from '../../hooks/useAuth';
import { getUserDisplayName, getUserBadge } from '../../utils/superAdminChecker';
import { useThemeContext } from '../../contexts/ThemeContext';
import NotificationPanel from './NotificationPanel';
import GlobalSearch from './GlobalSearch';
import useConnectedApps from '../../hooks/useConnectedApps';

const Header = ({ handleDrawerToggle }) => {
  const navigate = useNavigate();
  const { user, logout, authMode, isSuperAdmin } = useAuth();
  const { appName, isDarkMode, toggleDarkMode } = useThemeContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [switcherAnchor, setSwitcherAnchor] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { apps: suiteApps } = useConnectedApps();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const userBadge = getUserBadge(user);
  const displayName = getUserDisplayName(user);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        // Full-width header sits above the sidebar drawer (which starts
        // below it, not beside it) — must out-rank the drawer's own
        // z-index or a transient resize/collapse animation could render
        // the drawer on top of it. This is MUI's own documented recipe
        // for a "header spans full width, permanent drawer below it" layout.
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: isDarkMode ? 'rgba(15,20,35,0.75)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1, px: { xs: 1.5, sm: 2.5 } }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 1, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        {/* Brand */}
        <Box
          onClick={() => navigate('/dashboard')}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer', flexShrink: 0 }}
        >
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.25, overflow: 'hidden', flexShrink: 0,
            border: '1px solid', borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
            bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0.3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}>
            <img
              src="/logo.png"
              alt={appName || 'KoorAI ONE'}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </Box>
          <Typography sx={{
            fontWeight: 800, fontSize: '0.938rem', letterSpacing: '-0.01em',
            color: 'text.primary', whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}>
            {appName || 'KoorAI ONE'}
          </Typography>
        </Box>

        {/* Search + suite switcher — centered together as one group, with
            equal, consistent spacing between them, instead of the switcher
            living in a separate cluster on the far right. */}
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' }, justifyContent: 'center', alignItems: 'center', gap: 1.5 }}>
          <Box
            onClick={() => setSearchOpen(true)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              width: 420,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
              border: '1px solid',
              borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              color: 'text.disabled',
              cursor: 'pointer',
              transition: 'all 200ms',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                borderColor: 'primary.main',
                color: 'text.secondary',
              },
            }}
          >
            <SearchIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 400, flex: 1 }}>Search...</Typography>
            <Box sx={{
              px: 0.5, py: 0.125, borderRadius: 0.75,
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
              fontSize: '0.625rem', fontWeight: 600, color: 'text.disabled',
              lineHeight: 1.6,
            }}>
              ⌘K
            </Box>
          </Box>

          {/* Suite switcher */}
          <Tooltip title="KoorAI ONE apps" arrow>
            <IconButton
              onClick={(e) => setSwitcherAnchor(e.currentTarget)}
              size="small"
              sx={{
                color: 'text.disabled',
                width: 34, height: 34, flexShrink: 0,
                '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <AppsIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(switcherAnchor)}
            anchorEl={switcherAnchor}
            onClose={() => setSwitcherAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 300,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
              },
            }}
          >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.875rem', letterSpacing: '-0.01em' }}>
                {appName || 'KoorAI ONE'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                One platform. Every operation.
              </Typography>
            </Box>
            <Divider sx={{ mt: 1 }} />
            <Grid container spacing={0} sx={{ p: 1.5 }}>
              {/* Current app — ERP core module */}
              <Grid item xs={4}>
                <Box sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                  p: 1, borderRadius: 2, textAlign: 'center',
                  bgcolor: isDarkMode ? 'rgba(96,165,250,0.12)' : 'rgba(37,99,235,0.08)',
                  border: '1px solid', borderColor: isDarkMode ? 'rgba(96,165,250,0.25)' : 'rgba(37,99,235,0.18)',
                }}>
                  <ErpIcon sx={{ fontSize: 22, color: 'primary.main' }} />
                  <Typography sx={{ fontSize: '0.688rem', fontWeight: 700, color: 'primary.main' }}>ERP</Typography>
                  <Chip label="Current" size="small" sx={{ height: 16, fontSize: '0.625rem', fontWeight: 600 }} />
                </Box>
              </Grid>
              {suiteApps.map((app) => {
                const Icon = app.icon;
                return (
                  <Grid item xs={4} key={app.key}>
                    <Tooltip title={app.enabled ? app.description : `${app.description} — not enabled for your org`} arrow>
                      <Box
                        onClick={() => { if (app.enabled) { app.onClick(); setSwitcherAnchor(null); } }}
                        sx={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
                          p: 1, borderRadius: 2, textAlign: 'center',
                          cursor: app.enabled ? 'pointer' : 'not-allowed',
                          opacity: app.enabled ? 1 : 0.4,
                          transition: 'background 150ms',
                          '&:hover': app.enabled ? { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' } : {},
                        }}
                      >
                        <Icon sx={{ fontSize: 22, color: app.enabled ? app.color : 'text.disabled' }} />
                        <Typography sx={{ fontSize: '0.688rem', fontWeight: 700 }}>{app.label}</Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          </Popover>
        </Box>

        {/* Right side actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {/* Date indicator */}
          <Chip
            icon={<CalendarIcon sx={{ fontSize: '14px !important' }} />}
            label={dateStr}
            size="small"
            variant="outlined"
            sx={{
              display: { xs: 'none', lg: 'flex' },
              height: 28,
              fontSize: '0.688rem',
              fontWeight: 500,
              borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
              color: 'text.secondary',
            }}
          />

          {authMode === 'sso' && (
            <Chip
              label="SSO"
              size="small"
              sx={{
                display: { xs: 'none', md: 'flex' },
                height: 22,
                fontSize: '0.625rem',
                fontWeight: 600,
                bgcolor: 'rgba(46, 125, 50, 0.08)',
                color: 'success.main',
                border: '1px solid rgba(46, 125, 50, 0.2)',
              }}
            />
          )}

          <Tooltip title="Toggle fullscreen" arrow>
            <IconButton
              onClick={toggleFullscreen}
              size="small"
              sx={{
                color: 'text.disabled',
                width: 34, height: 34,
                '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              {isFullscreen ? <FullscreenExitIcon sx={{ fontSize: 18 }} /> : <FullscreenIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          <Tooltip title={isDarkMode ? 'Light mode' : 'Dark mode'} arrow>
            <IconButton
              onClick={toggleDarkMode}
              size="small"
              sx={{
                color: 'text.disabled',
                width: 34, height: 34,
                '&:hover': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              {isDarkMode ? <LightModeIcon sx={{ fontSize: 18 }} /> : <DarkModeIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>

          <NotificationPanel />

          {/* User Avatar + Dropdown */}
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              ml: 0.5,
              pl: 1,
              pr: 0.5,
              py: 0.5,
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'all 200ms',
              border: '1px solid transparent',
              '&:hover': {
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                border: '1px solid',
                borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              },
            }}
          >
            <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }}>
                {displayName}
              </Typography>
              <Typography sx={{ fontSize: '0.625rem', color: 'text.disabled', lineHeight: 1.2 }}>
                {userBadge?.label || user?.role || 'User'}
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 34, height: 34,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 700,
                border: '2px solid',
                borderColor: isDarkMode ? 'rgba(96,165,250,0.2)' : 'rgba(37,99,235,0.15)',
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                width: 240,
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              },
            }}
          >
            {/* User info header */}
            <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    width: 42, height: 42,
                    bgcolor: 'primary.main',
                    fontSize: '1rem',
                    fontWeight: 700,
                  }}
                >
                  {displayName.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{displayName}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.688rem' }}>{user?.email}</Typography>
                </Box>
              </Box>
              {userBadge && (
                <Chip
                  label={userBadge.label}
                  size="small"
                  sx={{
                    mt: 1,
                    height: 22,
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    bgcolor: userBadge.color === 'error' ? 'rgba(211,47,47,0.08)' : 'rgba(37,99,235,0.08)',
                    color: userBadge.color === 'error' ? 'error.main' : 'primary.main',
                    border: '1px solid',
                    borderColor: userBadge.color === 'error' ? 'rgba(211,47,47,0.2)' : 'rgba(37,99,235,0.2)',
                  }}
                />
              )}
            </Box>
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }} sx={{ py: 1, mt: 0.5 }}>
              <ListItemIcon><PersonIcon sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>My Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/help'); }} sx={{ py: 1 }}>
              <ListItemIcon><HelpOutline sx={{ fontSize: 18 }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Help & Support</ListItemText>
            </MenuItem>
            {(isSuperAdmin || user?.role === 'Admin') && (
              <MenuItem onClick={() => { setAnchorEl(null); navigate(isSuperAdmin ? '/super-admin' : '/admin'); }} sx={{ py: 1 }}>
                <ListItemIcon><SettingsIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Settings</ListItemText>
              </MenuItem>
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem
              onClick={() => { setAnchorEl(null); logout(); navigate('/login'); }}
              sx={{
                py: 1, mb: 0.5,
                color: 'error.main',
                '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' },
              }}
            >
              <ListItemIcon><LogoutIcon sx={{ fontSize: 18, color: 'error.main' }} /></ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Sign Out</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </AppBar>
  );
};

export default Header;
